import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./FreezedDatabase.css";

const API_BASE = import.meta.env.VITE_API_URL || "/api";
const ORDERS_API = `${API_BASE}/reservations`;

const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const FreezedDatabase = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unfreezingId, setUnfreezingId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const isSuperAdmin = localStorage.getItem("superAdminAuth") === "true";
    if (!isSuperAdmin) {
      navigate("/superadmin/login", { replace: true });
      return;
    }
    fetchFreezedOrders();
    // eslint-disable-next-line
  }, [navigate]);

  const fetchFreezedOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${ORDERS_API}?limit=1000`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const json = await res.json();
      const data = json.data || json || [];
      setOrders(data.filter((o) => o.frozen === true));
    } catch (err) {
      console.error("Failed to fetch frozen orders", err);
      setError(err.message);
      try {
        const saved = JSON.parse(localStorage.getItem("hexaplay_orders") || "[]");
        setOrders(saved.filter((o) => o.frozen === true));
      } catch {
        setOrders([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const syncLocal = (updater) => {
    try {
      const saved = JSON.parse(localStorage.getItem("hexaplay_orders") || "[]");
      localStorage.setItem("hexaplay_orders", JSON.stringify(updater(saved)));
    } catch {}
  };

  const handleUnfreeze = async (order) => {
    const id = order._id || order.id || order.bookingId;
    if (!window.confirm(`🔓 Are you sure you want to UNFREEZE booking for ${order.name}?\nThis will move it back to the active Admin Order List.`)) return;

    setUnfreezingId(id);
    try {
      const res = await fetch(`${ORDERS_API}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ frozen: false, frozenAt: null }),
      });
      if (!res.ok) throw new Error(`API failed: ${res.status}`);

      setOrders((prev) => prev.filter((o) => (o._id || o.id) !== id));
      syncLocal((saved) =>
        saved.map((o) => {
          const oid = o._id || o.id;
          return oid === id ? { ...o, frozen: false, frozenAt: null } : o;
        })
      );

      alert("✅ Booking unfreezed successfully! It will reappear in Admin Order List.");
    } catch (err) {
      alert(`❌ Failed to unfreeze: ${err.message}`);
    } finally {
      setUnfreezingId(null);
    }
  };

  return (
    <div className="freezed-page">
      <div className="freezed-header">
        <div>
          <h1>🔒 Freezed Database</h1>
          <p>Locked bookings accessible only by Super Admin</p>
        </div>
        <button className="back-btn" onClick={() => navigate("/superadmin/dashboard")}>
          ← Back to Dashboard
        </button>
      </div>

      {error && (
        <div className="freezed-error-banner">
          <span>⚠️ {error}. Showing cached data if available.</span>
          <button onClick={fetchFreezedOrders}>Retry</button>
        </div>
      )}

      <div className="freezed-stats">
        <div className="freezed-stat-card">
          <span>🔒</span>
          <div>
            <b>{orders.length}</b>
            <p>Frozen Bookings</p>
          </div>
        </div>
        <div className="freezed-stat-card green">
          <span>💰</span>
          <div>
            <b>₹{orders.reduce((a, o) => a + n(o.total), 0).toLocaleString("en-IN")}</b>
            <p>Total Frozen Value</p>
          </div>
        </div>
      </div>

      <div className="freezed-table-container">
        {loading ? (
          <div className="freezed-state">⏳ Loading frozen records...</div>
        ) : orders.length === 0 ? (
          <div className="freezed-state">📭 No frozen vml records found.</div>
        ) : (
          <table className="freezed-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Theater</th>
                <th>Date / Slot</th>
                <th>Amount</th>
                <th>Frozen At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => {
                const id = o._id || o.id || o.bookingId;
                const isProcessing = unfreezingId === id;
                return (
                  <tr key={id} className={isProcessing ? "processing" : ""}>
                    <td>{i + 1}</td>
                    <td>
                      <strong>{o.name}</strong><br />
                      <small>{o.whatsapp}</small>
                    </td>
                    <td>{o.theater}</td>
                    <td>
                      {o.date}<br />
                      <small>{o.timeSlot}</small>
                    </td>
                    <td>₹{n(o.total).toLocaleString("en-IN")}</td>
                    <td>{o.frozenAt ? new Date(o.frozenAt).toLocaleString() : "N/A"}</td>
                    <td>
                      <button
                        className="unfreeze-btn"
                        onClick={() => handleUnfreeze(o)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? "⏳ Processing..." : "🔓 Unfreeze"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FreezedDatabase;